// Thunks for Question Slice
import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../utils/constants/api";
import { ApiResponse } from "../../../types/APIResponse";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { EstablishmentTypeQuestion } from "../../../types";

// Fetch Questions
export const fetchEstablishmentTypeQuestions = createAsyncThunk<
    { data: EstablishmentTypeQuestion[]; total: number },
    { search_query: string; offset: number; limit: number; establishmentTypeId?: string },
    { rejectValue: string }
    >(
    'establishmentTypeQuestions/fetchEstablishmentTypeQuestions',
    async ({ search_query = '', offset = 0, limit = 10, establishmentTypeId }, { rejectWithValue }) => {
        try {
            const response: ApiResponse<EstablishmentTypeQuestion[]> & { counts: number } = await api.post(
                apiEndpoints.establishmentTypeQuestion.getAll,
                {
                    data: {
                        search_query,
                        offset,
                        limit,
                        establishmentTypeId,
                        order: {
                            "question.createdAt": "ASC",
                            "options.createdAt": "ASC"
                        }
                    },
                }
            );
            return { data: response.data, total: response.counts };
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch establishmentTypeQuestions');
        }
    }
);

// Create Question
export const createEstablishmentTypeQuestion = createAsyncThunk<EstablishmentTypeQuestion, EstablishmentTypeQuestion, { rejectValue: string }>("establishmentTypeQuestions/createEstablishmentTypeQuestion", async (requestData, { rejectWithValue }) => {
    try {
        const response = await api.post<ApiResponse<EstablishmentTypeQuestion>>(apiEndpoints.establishmentTypeQuestion.create, { data: requestData });
        return { ...response.data, ...requestData };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || "Failed to create establishmentTypeQuestion");
    }
});

// Update Question
export const updateEstablishmentTypeQuestion = createAsyncThunk<EstablishmentTypeQuestion, EstablishmentTypeQuestion, { rejectValue: string }>("establishmentTypeQuestions/updateEstablishmentTypeQuestion", async (requestData, { rejectWithValue }) => {
    try {
        const response = await api.put<ApiResponse<EstablishmentTypeQuestion>>(`${apiEndpoints.establishmentTypeQuestion.update}/${requestData.id}`, { data: requestData });
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || "Failed to update establishmentTypeQuestion");
    }
});

// Delete Question
export const deleteEstablishmentTypeQuestion = createAsyncThunk<{ id: string | number }, string | number, { rejectValue: string }>("establishmentTypeQuestions/deleteEstablishmentTypeQuestion", async (id, { rejectWithValue }) => {
    try {
        await api.delete(`${apiEndpoints.establishmentTypeQuestion.delete}/${id}`);
        return { id };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || "Failed to delete establishmentTypeQuestion");
    }
});

// Reorder Questions
export const reorderEstablishmentTypeQuestions = createAsyncThunk<
    void,
    { questions: { id: string; order: number }[] },
    { rejectValue: string }
>("establishmentTypeQuestions/reorderEstablishmentTypeQuestions", async ({ questions }, { rejectWithValue }) => {
    try {
        await api.put(apiEndpoints.establishmentTypeQuestion.reorder, { data: { questions } });
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || "Failed to reorder questions");
    }
});
